import { type ApiFetchContext, apiFetch } from "../client";
import type {
  ExpressCalc,
  ExpressCalcVoRaw,
  ExpressOrderDetail,
  ExpressOrderDetailRaw,
  ExpressSubmitParam,
} from "../types/express";
import type { LogisticsRoute, LogisticsRouteRaw } from "../types/logistics";
import type { LangCurrencyOpts } from "./logistics";
import { normalizeRoute } from "./logistics";

const toNumber = (v: unknown): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : Number(String(v).replace(/\s/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toMinor = (v: unknown): number => Math.round(toNumber(v) * 100);

function headerInit(opts: LangCurrencyOpts) {
  return {
    ...(opts.lang === undefined ? {} : { lang: opts.lang }),
    ...(opts.currency === undefined ? {} : { currency: opts.currency }),
  };
}

/**
 * GET /express/{expressOrderNo}/route2 — available logistics routes for an
 * already-created express order, bound to the delivery `addressId` and the
 * currently-selected packing services (services change the billable weight, so
 * the route fees recompute). Mirrors the legacy aps-web `expressLogisticsList`.
 *
 * `serviceItemIdList` is sent as a comma-joined query param exactly as aps-web
 * did. Reuses the shared `normalizeRoute` from the logistics endpoint so the
 * route card (`RouteLine`) renders identically to the rehearsal page.
 */
export async function listExpressRoutes(
  ctx: ApiFetchContext,
  authToken: string,
  input: { expressOrderNo: string; addressId: string; serviceItemIdList?: string[] },
  opts: LangCurrencyOpts = {},
): Promise<LogisticsRoute[]> {
  const qs = new URLSearchParams({ addressId: input.addressId });
  if (input.serviceItemIdList && input.serviceItemIdList.length > 0) {
    qs.set("serviceItemIdList", input.serviceItemIdList.join(","));
  }
  if (opts.currency) qs.set("currency", opts.currency);
  const raw = await apiFetch<LogisticsRouteRaw[]>(
    ctx,
    `/express/${encodeURIComponent(input.expressOrderNo)}/route2?${qs.toString()}`,
    { authToken, ...headerInit(opts) },
  );
  return (raw ?? []).map(normalizeRoute);
}

function normalizeCalc(raw: ExpressCalcVoRaw): ExpressCalc {
  const totalMinor = toMinor(raw.totalFee);
  const afterRaw = raw.totalFeeAfterDiscount;
  // Backend only fills totalFeeAfterDiscount when a coupon is applied.
  const totalAfterDiscountMinor =
    afterRaw === null || afterRaw === undefined || afterRaw === "" ? totalMinor : toMinor(afterRaw);
  return {
    logisticsFeeMinor: toMinor(raw.logisticsFee),
    operateFeeMinor: toMinor(raw.operateFee),
    serviceFeeMinor: toMinor(raw.serviceFee),
    fuelFeeMinor: toMinor(raw.fuelFee),
    insuranceFeeMinor: toMinor(raw.insuranceFee),
    couponFeeMinor: toMinor(raw.couponFee),
    totalMinor,
    totalAfterDiscountMinor,
    orderItemAmountMinor: toMinor(raw.orderItemAmount),
    currencyCode: raw.currencyCode ?? "RUB",
    currencySymbol: raw.currencySymbol ?? "₽",
    skipPrepaid: raw.skipPrepaid === true,
    // Backend default is carriable; only explicit false blocks submit.
    isCarriable: raw.isCarriable !== false,
    countryTips: raw.countryTips ?? null,
  };
}

/** Build the ExpressOrderSubmitParam body shared by /calculate and /submitV5. */
function submitBody(param: ExpressSubmitParam) {
  return {
    expressOrderNo: param.expressOrderNo,
    addressId: param.addressId,
    routeId: param.routeId,
    serviceItemIdList: param.serviceItemIdList,
    ...(param.insuranceId && param.insuranceId.length > 0
      ? { insuranceId: param.insuranceId }
      : {}),
    ...(param.couponCustomerId ? { couponCustomerId: param.couponCustomerId } : {}),
    ...(param.remarks ? { remarks: param.remarks } : {}),
    ...(param.taxNumber ? { taxNumber: param.taxNumber } : {}),
    ...(param.skipPrepaid ? { skipPrepaid: true } : {}),
  };
}

/**
 * POST /express/calculate — live fee breakdown for the current route + service
 * + insurance + coupon selection. Amounts come back in the request currency;
 * `normalizeCalc` stores them as integer minor units for the dual formatter.
 */
export async function calculateExpressFee(
  ctx: ApiFetchContext,
  authToken: string,
  param: ExpressSubmitParam,
  opts: LangCurrencyOpts = {},
): Promise<ExpressCalc> {
  const raw = await apiFetch<ExpressCalcVoRaw>(ctx, "/express/calculate", {
    method: "POST",
    authToken,
    ...headerInit(opts),
    body: submitBody(param),
  });
  return normalizeCalc(raw ?? {});
}

/**
 * POST /express/submitV5 — finalize the waybill (lock route/services/insurance/
 * coupon, generate the payable express order). Resolves to the express order
 * number to pay (`/pay?orderNo=…`). The backend re-validates ownership, INIT
 * status, address completeness (601) and route carriability.
 */
export async function submitExpressOrderV5(
  ctx: ApiFetchContext,
  authToken: string,
  param: ExpressSubmitParam,
  opts: LangCurrencyOpts = {},
): Promise<string> {
  return apiFetch<string>(ctx, "/express/submitV5", {
    method: "POST",
    authToken,
    ...headerInit(opts),
    body: submitBody(param),
  });
}

function normalizeDetail(raw: ExpressOrderDetailRaw): ExpressOrderDetail {
  const parcels = raw.parcelInfoList ?? [];
  const items = parcels.flatMap((p) =>
    (p.goodsItemList ?? []).map((g) => ({
      orderItemId: g.orderItemId ?? null,
      subject: g.subject ?? "",
      mainImg: g.mainImg ?? null,
      quantity: g.quantity ?? 1,
      skuNo: g.skuNo ?? null,
    })),
  );
  const totalWeightG = parcels.reduce((sum, p) => sum + (p.weight ?? 0), 0);
  const a = raw.expressAddressDetailVO ?? null;
  return {
    expressOrderNo: raw.expressOrderNo ?? "",
    status: raw.status ?? "",
    remarks: raw.remarks ?? null,
    address: a
      ? {
          recipientName: a.recipientName ?? null,
          countryCode: a.countryCode ?? null,
          countryText: a.countryText ?? null,
          provinceText: a.provinceText ?? null,
          cityText: a.cityText ?? null,
          areaText: a.areaText ?? null,
          addressDetail: a.addressDetail ?? null,
          postalCode: a.postalCode ?? null,
        }
      : null,
    items,
    totalWeightG,
  };
}

/**
 * GET /express/detail — read-only express-order detail (items recap + bound
 * recipient address + status). Used to render the parcel-contents panel after
 * navigating in with `?expressOrderNo=`. Source-platform titles are passed
 * through untranslated.
 */
export async function getExpressOrderDetail(
  ctx: ApiFetchContext,
  authToken: string,
  expressOrderNo: string,
  opts: LangCurrencyOpts = {},
): Promise<ExpressOrderDetail> {
  const raw = await apiFetch<ExpressOrderDetailRaw>(
    ctx,
    `/express/detail?expressOrderNo=${encodeURIComponent(expressOrderNo)}`,
    { authToken, ...headerInit(opts) },
  );
  return normalizeDetail(raw ?? {});
}
